<template>
  <modal
    :title="modalTitle"
    :value="show"
    @cancel="closeModal"
    effect="fade/zoom"
  >
    <Loader :is-visible="loading" />

    <template v-if="dynamicContent">
      <div class="row">
        <div class="col-sm-12 form-group">
          <el-switch
            v-model="toSchedule"
            inactive-text=""
            active-text="Planifier l'envoi"
            size="mini"
          />
        </div>

        <template v-if="toSchedule">
          <div class="col-sm-12 form-group">
            <label>Planifier pour le</label>
            <Datepicker
              v-model="form.sendDate"
              format="dd.MM.yyyy"
              language="fr"
              input-class="form-control"
            ></Datepicker>
            <small
              v-if="errors && errors.sendDate"
              class="form-text text-danger"
            >
              {{ errors.sendDate }}</small
            >
          </div>

          <div class="col-sm-12 form-group">
            <label>Heure</label>
            <el-select v-model="form.sendHour" class="w-100">
              <el-option
                v-for="hourOption in hourOptions"
                :key="`schedule-hour-${hourOption.value}`"
                :value="hourOption.value"
                :label="hourOption.label"
              />
            </el-select>
            <small
              v-if="errors && errors.sendHour"
              class="form-text text-danger"
            >
              {{ errors.sendHour }}</small
            >
          </div>
        </template>

        <div class="col-sm-12 form-group d-flex flex-column mt-1">
          <label class="font-weight-bold">Envoyer une notification ?</label>
          <el-switch
            v-model="form.sendNotification"
            inactive-text="Non"
            active-text="Oui"
            size="mini"
            class="input-switch"
          />
        </div>

        <div v-if="hasUserFilters" class="col-sm-12">
          <hr class="w-100" />
          <div class="h5">Filtres coachs</div>

          <div class="row">
            <div
              v-if="userFiltersCoachs.length > 0"
              class="col-sm-6 form-group d-flex flex-column"
            >
              <label>Coachs</label>
              <div class="d-flex flex-wrap" style="gap: 0.25rem">
                <div
                  v-for="coach in userFiltersCoachs"
                  :key="`user-filters-email-${coach.value}`"
                  class="badge badge-success"
                >
                  {{
                    coach.label.length > 32
                      ? `${coach.label.substring(0, 32)}...`
                      : coach.label
                  }}
                </div>
              </div>
            </div>

            <div
              v-if="userFiltersStatus.length > 0"
              class="col-sm-6 form-group d-flex flex-column"
            >
              <label>Statut</label>
              <div class="d-flex flex-wrap" style="gap: 0.25rem">
                <div
                  v-for="status in userFiltersStatus"
                  :key="`user-filters-status-${status.value}`"
                  class="badge"
                  :style="`background-color: ${status.color}`"
                >
                  {{ status.label }}
                </div>
              </div>
            </div>

            <div
              v-if="userFiltersGender"
              class="col-sm-6 form-group d-flex flex-column"
            >
              <label>Genre</label>
              <div class="badge badge-success" style="width: fit-content">
                {{ userFiltersGender }}
              </div>
            </div>

            <div
              v-if="userFiltersZipcodes.length > 0"
              class="col-sm-6 form-group d-flex flex-column"
            >
              <label>Département</label>
              <div class="d-flex flex-wrap" style="gap: 0.25rem">
                <div
                  v-for="zipcode in userFiltersZipcodes"
                  :key="`user-filters-zipcode-${zipcode.value}`"
                  class="badge badge-success"
                >
                  {{ zipcode.label }}
                </div>
              </div>
            </div>

            <div
              v-if="userFiltersB2bOnly"
              class="col-sm-6 form-group d-flex flex-column"
            >
              <label>B2B</label>
              <div class="badge badge-success" style="width: fit-content">
                B2B seulement
              </div>
            </div>
          </div>
        </div>
      </div>

      <div slot="modal-footer" class="modal-footer">
        <button
          type="button"
          class="btn btn-default"
          :disabled="loading"
          @click="closeModal"
        >
          Annuler
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="loading"
          @click="toSchedule ? schedule() : send()"
        >
          {{ toSchedule ? "Programmer" : "Envoyer" }}
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import Modal from "vue-strap/src/Modal";
import Loader from "@/components/utils/Loader";
import Datepicker from "vuejs-datepicker";

export default {
  components: {
    Modal,
    Loader,
    Datepicker,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },

    dynamicContent: {
      type: Object,
      default: null,
    },

    type: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      form: {
        sendDate: null,
        sendHour: null,
        sendNotification: false,
      },
      errors: {},
      toSchedule: false,
      hourOptions: [
        { label: "10h", value: 10 },
        { label: "12h", value: 12 },
        { label: "18h", value: 18 },
        { label: "20h", value: 20 },
      ],
    };
  },

  computed: {
    ...mapGetters({
      coachsStatusOptions: "getCoachStatus",
      departments: "getDepartments",
    }),

    modalTitle() {
      let title = this.toSchedule ? "Programmer" : "Envoyer";

      switch (this.type) {
        case "COACH_SPECIAL_EVENT":
          title += " un événement";
          break;
        case "MAJ_COACH_APP":
          title += " une note de mise à jour";
          break;
        case "TUTO_COACH_APP":
          title += " un tutoriel";
          break;
        case "CLIENT_APP_NEWS":
          title += " une actualité";
          break;
        default:
          title += " un contenu dynamique";
      }

      return title;
    },

    zipcodesOptions() {
      return this.departments.map((dpt) => ({
        label: dpt.code !== "all" ? `${dpt.code} - ${dpt.name}` : dpt.name,
        value: dpt.code,
      }));
    },

    hasUserFilters() {
      return (
        this.dynamicContent?.userFilters &&
        Object.values(this.dynamicContent.userFilters).some((v) =>
          Array.isArray(v) ? v.length > 0 : !!v,
        )
      );
    },

    userFiltersCoachs() {
      return this.dynamicContent?.userFilters?.emails || [];
    },

    userFiltersStatus() {
      return (
        this.dynamicContent?.userFilters?.status
          ?.filter((s) =>
            this.coachsStatusOptions.some((opt) => opt.value === s),
          )
          .map((s) =>
            this.coachsStatusOptions.find((opt) => opt.value === s),
          ) || []
      );
    },

    userFiltersGender() {
      return this.dynamicContent?.userFilters?.gender;
    },

    userFiltersZipcodes() {
      return (
        this.dynamicContent?.userFilters?.zipcodes
          ?.filter((z) => this.zipcodesOptions.some((opt) => opt.value === z))
          .map((z) => this.zipcodesOptions.find((opt) => opt.value === z)) || []
      );
    },

    userFiltersB2bOnly() {
      return !!this.dynamicContent?.userFilters?.b2bOnly;
    },
  },

  watch: {
    dynamicContent() {
      if (this.dynamicContent) {
        this.form = {
          sendDate: this.dynamicContent.autoSendAt
            ? moment(this.dynamicContent.autoSendAt).toDate()
            : null,
          sendHour: this.dynamicContent.autoSendAt
            ? moment(this.dynamicContent.autoSendAt).hour()
            : null,
          sendNotification: !!this.dynamicContent.autoSendNotification,
        };

        this.toSchedule =
          !!this.dynamicContent.toSchedule || !!this.dynamicContent.autoSendAt;
      } else {
        this.resetForm();
      }
    },
  },

  methods: {
    resetForm() {
      this.form = {
        sendAt: null,
        sendNotification: false,
      };
      this.errors = {};
      this.toSchedule = false;
    },

    closeModal() {
      this.$emit("close");
      this.resetForm();
    },

    checkFields() {
      this.errors = {};

      if (this.toSchedule) {
        if (!this.form.sendDate) {
          this.errors.sendDate = "La date d'envoi est requise";
        } else if (moment(this.form.sendDate).isBefore(moment(), "day")) {
          this.errors.sendDate = "La date d'envoi doit être dans le futur";
        }

        if (!this.form.sendHour) {
          this.errors.sendHour = "L'heure d'envoi est requise";
        } else if (
          !this.hourOptions.map((opt) => opt.value).includes(this.form.sendHour)
        ) {
          this.errors.sendHour =
            "L'heure d'envoi doit être l'une des options disponibles";
        } else if (this.form.sendDate) {
          const sendAt = moment(this.form.sendDate)
            .hour(this.form.sendHour)
            .minute(0)
            .second(0);

          if (sendAt.isBefore(moment())) {
            this.errors.sendDate =
              "La date et l'heure d'envoi doivent être dans le futur";
            this.errors.sendHour =
              "La date et l'heure d'envoi doivent être dans le futur";
          }
        }
      }

      return Object.keys(this.errors).length === 0;
    },

    async send() {
      const isValid = this.checkFields();

      if (!isValid) {
        return;
      }

      try {
        this.loading = true;

        const { data } = await this.$api.post(
          `/dynamic-contents/${this.dynamicContent.id}/send`,
          {
            sendNotification: this.form.sendNotification,
          },
        );

        this.loading = false;

        this.$emit("sent", data);
        this.closeModal();
        this.$successToast("Envoyé avec succès");
      } catch (e) {
        this.$errorToast("Impossible d'envoyer le contenu", `(${e.message})`);
      } finally {
        this.loading = false;
      }
    },

    async schedule() {
      const isValid = this.checkFields();

      if (!isValid) {
        return;
      }

      try {
        this.loading = true;

        const autoSendAt = moment(this.form.sendDate)
          .hour(this.form.sendHour)
          .minute(0)
          .second(0)
          .toDate();

        const { data } = await this.$api.patch(
          `/dynamic-contents/${this.dynamicContent.id}/auto-send`,
          {
            autoSendAt,
            autoSendNotification: this.form.sendNotification,
          },
        );

        this.loading = false;

        this.$emit("scheduled", data);
        this.closeModal();
        this.$successToast("Envoi programmé avec succès");
      } catch (e) {
        this.$errorToast("Impossible de planifier l'envoi", `(${e.message})`);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
